import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { mediaApi } from './api';
import { keys } from '@/lib/query/keys';
import { useListState, useLocalListState, type ListState } from '@/lib/hooks/useListState';
import { useSearchBox } from '@/lib/hooks/useSearchBox';
import { DataView, EmptyState } from '@/lib/ui/states';
import { FilterBar } from '@/lib/ui/FilterBar';
import { Pagination } from '@/lib/ui/Pagination';
import { Button } from '@/lib/ui/Button';
import { Select } from '@/lib/ui/Select';
import { TextInput } from '@/lib/ui/Field';
import { cn } from '@/lib/ui/cn';
import { MonoValue } from '@/lib/ui/Typography';
import { formatBytes } from '@/lib/format';
import type { MediaAsset, MediaKind } from '@/lib/api/types';
import { UploadDropzone } from './UploadDropzone';
import { previewUrl } from '@/lib/media/derivatives';

const KIND_OPTIONS = [
  { value: '', labelKey: 'media.kindAll' },
  { value: 'image', labelKey: 'media.kindImages' },
  { value: 'svg', labelKey: 'media.kindSvg' },
  { value: 'pdf', labelKey: 'media.kindPdf' },
  { value: 'doc', labelKey: 'media.kindDocuments' },
  { value: 'video', labelKey: 'media.kindVideo' },
];

const KIND_ICON: Record<MediaKind, string> = {
  image: '🖼',
  svg: '⬡',
  pdf: '📄',
  doc: '📝',
  video: '▶',
};

/** The one filter this grid owns; a module constant so its identity is stable. */
const KIND_FILTER = { kind: '' };

export interface MediaBrowserProps {
  onSelect?: (asset: MediaAsset) => void;
  canUpload?: boolean;
  selectedId?: string | null;
  /** Rendered inside the picker modal rather than as the /media page. */
  inModal?: boolean;
  /** Uploads made here are trimmed before they are stored (the opening field's `x-media-trim`). */
  uploadTrim?: 'transparent';
}

/**
 * Reusable media grid (browser + picker modes). Lazy thumbnails, filters, debounced search.
 *
 * `inModal` is not cosmetic, and it selects a COMPONENT rather than a flag on a
 * hook. PHASE 13 moved list state into the address bar, and the picker is mounted
 * over whatever screen you were on — so a picker page-turn would otherwise rewrite
 * the *host* list's `page` and leave `kind=` behind in a URL that no longer has a
 * picker in it. Routed use keeps the URL; the picker keeps its own state, and
 * because that is a different hook in a different component, the modal grid holds
 * no dependency on a Router at all (F-28).
 */
export function MediaBrowser({ inModal, ...props }: MediaBrowserProps) {
  return inModal ? <ModalMediaBrowser {...props} /> : <RoutedMediaBrowser {...props} />;
}

/** The /media page: this grid IS the address, so its state lives there. */
function RoutedMediaBrowser(props: MediaBrowserProps) {
  const list = useListState(KIND_FILTER);
  return <MediaBrowserView list={list} {...props} />;
}

/** The picker modal: a transient tool over someone else's address. */
function ModalMediaBrowser(props: MediaBrowserProps) {
  const list = useLocalListState(KIND_FILTER);
  return <MediaBrowserView list={list} {...props} />;
}

function MediaBrowserView({
  list,
  onSelect,
  canUpload,
  selectedId,
  uploadTrim,
}: MediaBrowserProps & { list: ListState }) {
  const { t } = useTranslation();
  const [rawSearch, setRawSearch] = useSearchBox(list.search, list.setSearch);

  const query = useQuery({
    queryKey: keys.media.list(list.params),
    queryFn: () => mediaApi.list(list.params),
    placeholderData: (prev) => prev,
  });

  return (
    <div className="flex flex-col gap-md">
      {canUpload && <UploadDropzone trim={uploadTrim} />}
      <FilterBar
        className="items-end"
        fields={[]}
        filters={[]}
        onAdd={() => {}}
        onRemove={() => {}}
        active={list.active}
        onClearAll={() => {
          setRawSearch('');
          list.reset();
        }}
      >
        <div className="w-[160px]">
          <Select
            label={t('media.kind')}
            value={(list.filters.kind as string) || ''}
            onValueChange={(v) => list.setFilter('kind', v)}
            options={KIND_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
          />
        </div>
        <div className="min-w-[200px] flex-1">
          <TextInput
            label={t('common.search')}
            placeholder={t('media.searchPlaceholder')}
            value={rawSearch}
            onChange={(e) => setRawSearch(e.target.value)}
          />
        </div>
      </FilterBar>

      <DataView
        query={query}
        isEmpty={(d) => d.data.length === 0}
        copy={{ loading: t('media.loading'), errorTitle: t('media.loadFailed') }}
        empty={
          /* "Nothing here" and "nothing matches" are different facts and lead to
             different next actions — upload something, or widen the filter. */
          list.active ? (
            <EmptyState
              title={t('media.noMatchTitle')}
              body={t('media.noMatchBody')}
              action={
                <Button
                  variant="ghost"
                  onClick={() => {
                    setRawSearch('');
                    list.reset();
                  }}
                >
                  {t('ui.clearAll')}
                </Button>
              }
            />
          ) : (
            <EmptyState
              title={t('media.emptyTitle')}
              body={canUpload ? t('media.emptyBodyUpload') : t('media.emptyBody')}
            />
          )
        }
      >
        {(d) => (
          <>
            <ul className="grid grid-cols-2 gap-md sm:grid-cols-3 lg:grid-cols-4">
              {d.data.map((asset) => (
                <li key={asset.id}>
                  <button
                    onClick={() => onSelect?.(asset)}
                    className={cn(
                      'group flex w-full flex-col overflow-hidden rounded-md border-[0.5px] border-hairline bg-canvas-paper text-start transition-transform duration-almanac ease-almanac',
                      onSelect && 'cursor-pointer hover:translate-y-[-2px] hover:border-accent-blue',
                      selectedId === asset.id && 'border-accent-blue ring-2 ring-accent-blue/30',
                    )}
                  >
                    <div className="flex aspect-4/3 items-center justify-center bg-canvas-deep-sand">
                      {asset.kind === 'image' && !asset.is_restricted ? (
                        <img
                          /* The generated thumb, by label — never by index (QA F-13). */
                          src={previewUrl(asset)}
                          alt={asset.alt?.en ?? asset.file_name}
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="text-[2rem]" aria-hidden>
                          {KIND_ICON[asset.kind]}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col gap-[2px] p-sm">
                      <span className="truncate text-small text-ink-graphite">{asset.file_name}</span>
                      <span className="font-mono text-label-sm text-ink-mute">
                        {asset.width > 0 && (
                          <MonoValue>
                            {asset.width}×{asset.height}
                          </MonoValue>
                        )}{' '}
                        · {formatBytes(asset.size_bytes)}
                        {asset.is_restricted && ' · 🔒'}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
            {d.meta?.pagination && (
              <Pagination
                meta={d.meta.pagination}
                onPageChange={list.setPage}
                onPerPageChange={list.setPerPage}
              />
            )}
          </>
        )}
      </DataView>
    </div>
  );
}
